import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import PlayerPicker from "./PlayerPicker";
import StatsFilters from "./StatsFilters";
import { CategoryChart, Timeline } from "./AnalyticsCharts";
import ReportButtons from "./ReportButtons";
import { EMPTY_FILTERS, STATUS, errMsg, niceDate, pill, rangeText, ready, shortLabel } from "@/lib/scoring";

const Card = ({ label, value, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
    <p data-testid={testid} className="mt-1 text-2xl font-extrabold tabular-nums text-[#0B0F19]">{value}</p>
  </div>
);

function History({ rows, outcomes, onOpen }) {
  return (
    <div data-testid="session-history" className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
      <p className="px-5 pt-4 text-sm font-extrabold text-[#0B0F19]">Session history</p>
      <table className="mt-2 min-w-full text-sm">
        <thead><tr className="border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
          {["Date", "Session", "Program", "Group", "Total", "Avg", "Attempts", ...outcomes.map(shortLabel)].map((h, i) => <th key={h} className={`px-3 py-2 font-bold whitespace-nowrap ${i > 3 ? "text-right" : "text-left"}`}>{h}</th>)}
        </tr></thead>
        <tbody>
          {!rows.length && <tr><td colSpan={7 + outcomes.length} className="px-4 py-6 text-center text-slate-500">No sessions in this date range.</td></tr>}
          {[...rows].reverse().map((h) => (
            <tr key={h.session_id} data-testid={`history-${h.session_id}`} onClick={() => onOpen(h.session_id)} className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-[#2563EB]/[0.04]">
              <td className="px-3 py-2.5 whitespace-nowrap font-semibold">{niceDate(h.date)}</td>
              <td className="px-3 py-2.5 text-[#2563EB] font-bold">{h.name}{h.status !== "closed" && <span className="ml-1 text-[10px] text-slate-400">({STATUS[h.status]})</span>}</td>
              <td className="px-3 py-2.5 text-slate-600">{h.program || "—"}</td><td className="px-3 py-2.5 text-slate-600">{h.group || "—"}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{h.total}</td><td className="px-3 py-2.5 text-right tabular-nums font-extrabold text-[#2563EB]">{h.avg}</td><td className="px-3 py-2.5 text-right tabular-nums">{h.attempts}</td>
              {outcomes.map((o) => <td key={o.key} className="px-3 py-2.5 text-right tabular-nums">{h.counts[o.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PlayerAnalytics({ api, ops, admin, target, setTarget, onOpenSession }) {
  const [f, setF] = useState({ ...EMPTY_FILTERS });
  const [meta, setMeta] = useState(null);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const { pid, discipline: disc } = target;
  useEffect(() => { api.get("/scoring/stats/filters", { discipline: disc }).then(setMeta); }, [api, disc]);
  useEffect(() => {
    if (!pid || !ready(f)) return;
    setErr("");
    api.get(`/scoring/stats/players/${pid}`, { discipline: disc, range: f.range, start: f.start, end: f.end, session_id: f.session_id }).then(setD).catch((e) => setErr(errMsg(e)));
  }, [api, pid, disc, f]);
  const o = d?.overview;
  return (
    <div data-testid="player-analytics" className="space-y-5">
      <div className="grid md:grid-cols-[1fr_auto] gap-3 items-start">
        <PlayerPicker api={api} multi={false} onPick={(p) => setTarget({ pid: p.id, discipline: disc })} />
        <div className="flex gap-2">{["hitting", "pitching"].map((x) => <button key={x} data-testid={`pa-disc-${x}`} onClick={() => setTarget({ pid, discipline: x })} className={pill(disc === x)}>{x === "hitting" ? "Hitting" : "Pitching"}</button>)}</div>
      </div>
      {!pid && <p data-testid="pa-empty" className="text-sm text-slate-500">Choose a player above, or click a name on the leaderboard.</p>}
      {pid && <StatsFilters f={f} setF={setF} meta={meta} full={false} idp="pa" />}
      {err && <p data-testid="pa-error" className="text-sm font-semibold text-red-600">{err}</p>}
      {pid && !d && !err && <Loader2 className="animate-spin text-slate-400" />}
      {pid && d && d.player.id === pid && <>
        <div>
          <h2 data-testid="pa-name" className="text-2xl md:text-3xl font-extrabold tracking-tight text-[#0B0F19]">{d.player.name}</h2>
          <p className="mt-1 text-sm text-slate-500">{d.player.age_group || "No age division"} · {d.player.groups.length ? d.player.groups.join(", ") : "No groups"} · <b className="text-[#0B0F19]">{rangeText(f, d.range)}</b></p>
          <div className="mt-3"><ReportButtons ops={ops} admin={admin} pid={pid} discipline={disc} /></div>
          {!o.attempts && <p data-testid="pa-no-data" className="mt-3 text-sm text-slate-500">No completed {disc} sessions recorded for this athlete in these dates yet.</p>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card label="Sessions" value={o.sessions} testid="pa-sessions" /><Card label="Attempts" value={o.attempts} testid="pa-attempts" />
          <Card label="Total score" value={o.total} testid="pa-total" /><Card label="Average" value={o.avg} testid="pa-avg" /><Card label="Best session avg" value={o.best_avg} testid="pa-best" />
          <Card label={`Last session${o.last_date ? ` · ${niceDate(o.last_date)}` : ""}`} value={o.last_total} testid="pa-last-total" /><Card label="Last session avg" value={o.last_avg} testid="pa-last-avg" />
          {disc === "pitching" && <><Card label="Strike %" value={`${o.strike_pct}%`} testid="pa-strike-pct" /><Card label="Located strike %" value={`${o.located_strike_pct}%`} testid="pa-located-pct" /><Card label="Ball %" value={`${o.ball_pct}%`} testid="pa-ball-pct" /></>}
          <Card label="Lifetime avg" value={d.lifetime.avg} testid="pa-life-avg" />
        </div>
        <div className="grid xl:grid-cols-2 gap-5"><CategoryChart data={d} outcomes={d.outcomes} /><Timeline data={d} outcomes={d.outcomes} /></div>
        <History rows={d.history} outcomes={d.outcomes} onOpen={onOpenSession} />
      </>}
    </div>
  );
}
